import { motion, useReducedMotion } from 'framer-motion'
import * as React from 'react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export type ComboboxOption = {
  label: string
  value: string
  description?: string
}

type ComboboxProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'defaultValue'> & {
  options: ComboboxOption[]
  value?: string
  defaultValue?: string
  placeholder?: string
  emptyMessage?: string
  disabled?: boolean
  inputId?: string
  onChange?: (value: string, option: ComboboxOption) => void
}

function highlight(label: string, query: string) {
  if (!query) return label
  const index = label.toLowerCase().indexOf(query.toLowerCase())
  if (index === -1) return label
  return (
    <>
      {label.slice(0, index)}
      <mark className="bg-transparent font-semibold text-foreground">
        {label.slice(index, index + query.length)}
      </mark>
      {label.slice(index + query.length)}
    </>
  )
}

const Combobox = React.forwardRef<HTMLDivElement, ComboboxProps>(
  (
    {
      options,
      value: controlledValue,
      defaultValue,
      placeholder = 'Search…',
      emptyMessage = 'No results',
      disabled = false,
      inputId,
      onChange,
      className,
      ...props
    },
    ref
  ) => {
    const reduceMotion = useReducedMotion()
    const listboxId = React.useId()
    const isControlled = controlledValue !== undefined
    const [internalValue, setInternalValue] = React.useState(defaultValue ?? '')
    const value = isControlled ? controlledValue : internalValue
    const selectedOption = options.find((option) => option.value === value)
    const [query, setQuery] = React.useState('')
    const [open, setOpen] = React.useState(false)
    const [active, setActive] = React.useState(0)
    const rootRef = React.useRef<HTMLDivElement>(null)
    const inputRef = React.useRef<HTMLInputElement>(null)

    React.useImperativeHandle(ref, () => rootRef.current as HTMLDivElement)

    const results = options.filter((option) =>
      option.label.toLowerCase().includes(query.toLowerCase())
    )
    const commit = (option: ComboboxOption) => {
      if (!isControlled) setInternalValue(option.value)
      onChange?.(option.value, option)
      setQuery('')
      setOpen(false)
      inputRef.current?.blur()
    }
    const transition = reduceMotion
      ? { duration: 0 }
      : { type: 'spring' as const, stiffness: 520, damping: 32 }

    return (
      <div ref={rootRef} className={cn('magic-choice relative w-full', className)} {...props}>
        <Popover
          open={open && !disabled}
          onOpenChange={(nextOpen) => setOpen(disabled ? false : nextOpen)}
        >
          <div className="relative">
            <PopoverTrigger asChild>
              <input
                ref={inputRef}
                id={inputId}
                role="combobox"
                aria-expanded={open && !disabled}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={
                  open && results[active] ? `${listboxId}-${active}` : undefined
                }
                value={open ? query : (selectedOption?.label ?? query)}
                placeholder={placeholder}
                disabled={disabled}
                onChange={(event) => {
                  setQuery(event.target.value)
                  setActive(0)
                  setOpen(true)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown') {
                    event.preventDefault()
                    setOpen(true)
                    setActive((current) => Math.min(current + 1, Math.max(results.length - 1, 0)))
                  } else if (event.key === 'ArrowUp') {
                    event.preventDefault()
                    setActive((current) => Math.max(current - 1, 0))
                  } else if (event.key === 'Enter' && open && results[active]) {
                    event.preventDefault()
                    commit(results[active])
                  } else if (event.key === 'Escape') {
                    setOpen(false)
                  }
                }}
                className="magic-choice__control h-9 w-full rounded-xl border border-border bg-background px-3 py-2 pr-9 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </PopoverTrigger>
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 21l-4.3-4.3M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z" />
            </svg>
          </div>
          {open && !disabled && (
            <PopoverContent
              align="start"
              sideOffset={8}
              collisionPadding={16}
              onOpenAutoFocus={(event) => event.preventDefault()}
              onCloseAutoFocus={(event) => event.preventDefault()}
              className="max-h-[min(18rem,var(--radix-popover-content-available-height))] w-[var(--radix-popover-trigger-width)] overflow-y-auto rounded-xl p-1 shadow-xl"
            >
              <motion.ul
                id={listboxId}
                role="listbox"
                initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={transition}
                className="origin-top"
              >
                {results.length === 0 && (
                  <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                    {emptyMessage}
                  </li>
                )}
                {results.map((option, index) => {
                  const isSelected = option.value === value
                  return (
                    <motion.li
                      key={option.value}
                      id={`${listboxId}-${index}`}
                      role="option"
                      aria-selected={isSelected}
                      initial={reduceMotion ? false : { opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: reduceMotion ? 0 : index * 0.02 }}
                      onMouseEnter={() => setActive(index)}
                      onPointerDown={(event) => {
                        event.preventDefault()
                        commit(option)
                      }}
                      className={cn(
                        'flex cursor-pointer items-start gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                        active === index && 'bg-accent text-accent-foreground'
                      )}
                    >
                      <span className="flex-1">
                        <span className="block">{highlight(option.label, query)}</span>
                        {option.description && (
                          <span className="block text-xs text-muted-foreground">
                            {option.description}
                          </span>
                        )}
                      </span>
                      {isSelected && (
                        <svg
                          aria-hidden="true"
                          viewBox="0 0 24 24"
                          className="mt-0.5 size-4 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      )}
                    </motion.li>
                  )
                })}
              </motion.ul>
            </PopoverContent>
          )}
        </Popover>
      </div>
    )
  }
)
Combobox.displayName = 'Combobox'

export { Combobox }
